import { Component, inject } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { TranslocoDirective } from '@jsverse/transloco';
import { DateFormatPipe } from '@processpuzzle/base-entity';

export interface CancelSubscriptionDialogData {
  orgKey: string;
  /** When service actually stops. The one fact this dialog exists to state. */
  currentPeriodEnd: string | undefined;
}

/**
 * Confirms a cancellation, and says plainly what cancelling does.
 *
 * **The wording is the whole point of this component.** The operation it confirms cancels *at the
 * end of the paid period*: the status stays as it was, service continues to `currentPeriodEnd`, and
 * only `cancelAtPeriodEnd` changes. A dialog that said "your subscription will be cancelled" would
 * be describing a different operation from the one it calls — and a customer who believed it would
 * either think they had lost access they still have, or ask support why they still have it.
 *
 * It also says the cancellation is reversible until that date, because it is: `resumeOwnSubscription`
 * exists precisely so that a misclick here does not need a support ticket.
 *
 * **Its own component rather than core's `DeleteConfirmationDialog`.** That one is not exported from
 * `@processpuzzle/base-entity`, so reaching it would mean a deep import into another library's
 * internals — and it would be the wrong dialog anyway: it confirms an irreversible deletion, which
 * is the opposite of what this is.
 */
@Component({
  selector: 'pp-cancel-subscription-dialog',
  imports: [MatDialogTitle, MatDialogContent, MatDialogActions, MatButton, MatIcon, TranslocoDirective, DateFormatPipe],
  template: `
    <ng-container *transloco="let t; prefix: 'platform_subscription.portal.cancel_dialog'">
      <h2 mat-dialog-title>{{ t('title') }}</h2>
      <mat-dialog-content>
        <!-- Service continues. Stated first, because it is the thing a customer clicking Cancel is
             most likely to have assumed otherwise. -->
        <p class="cancel-lead" test-id="cancel-dialog-period-end">
          <mat-icon>schedule</mat-icon>
          <span>{{ data.currentPeriodEnd ? t('untilPeriodEnd', { date: data.currentPeriodEnd | ppDate }) : t('untilPeriodEndUnknown') }}</span>
        </p>
        <p>{{ t('reversible') }}</p>
        <p class="cancel-note">{{ t('dataNote', { orgKey: data.orgKey }) }}</p>
      </mat-dialog-content>
      <mat-dialog-actions align="end">
        <button id="cancel-dialog-keep" type="button" mat-button test-id="cancel-dialog-keep" (click)="onKeep()">{{ t('keep') }}</button>
        <button id="cancel-dialog-confirm" type="button" mat-raised-button color="warn" test-id="cancel-dialog-confirm" (click)="onConfirm()">
          {{ t('confirm') }}
        </button>
      </mat-dialog-actions>
    </ng-container>
  `,
  styles: [
    `
      .cancel-lead {
        display: flex;
        align-items: flex-start;
        gap: 8px;
        font-weight: 600;
      }

      .cancel-note {
        font-size: 0.85rem;
        opacity: 0.75;
      }
    `,
  ],
})
export class CancelSubscriptionDialog {
  readonly data = inject<CancelSubscriptionDialogData>(MAT_DIALOG_DATA);
  private readonly dialogRef = inject(MatDialogRef<CancelSubscriptionDialog, boolean>);

  onKeep(): void {
    this.dialogRef.close(false);
  }

  onConfirm(): void {
    this.dialogRef.close(true);
  }
}
